/**
 * Memoizes a pure mapping by source-object identity (WeakMap-backed).
 *
 * With React Query's structural sharing, unchanged source objects keep their
 * reference across refetches — so the mapped object keeps its identity too,
 * preserving downstream memo()/useMemo work instead of busting it with a fresh
 * object per recompute. Cache entries are GC'd together with their source.
 *
 * The mapper must be pure and must not return undefined (an undefined result
 * would be recomputed on every call).
 */
export function weakMemoize<S extends object, R>(map: (source: S) => R): (source: S) => R {
  const cache = new WeakMap<S, R>()
  return (source) => {
    const cached = cache.get(source)
    if (cached !== undefined) return cached
    const result = map(source)
    cache.set(source, result)
    return result
  }
}
